import React, { useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Callout, useOverlayContext } from 'seagull';
import { useIoContext, useStoreActions } from "../../../../contexts";
import { serviceCostModel } from "../../../../models";
import ManualBillModal from "../../../../components/ManualBillModal/ManualBillModal";
import { getBillServiceFailureToastText, getServiceSuccessToastText } from "./BillServiceModal.utils";
import CumulativeWarningCallout from "../../../../components/BillClientModal/Callouts/CumulativeWarningCallout";
import BillServiceTableItem from "../../../../components/BillClientModal/BillServiceTableItem/BillServiceTableItem";
import { utils } from "../../../../utils";
import { isSameDay } from 'date-fns';
import styles from "./BillServiceModal.module.scss";
import { handleRequest, sendErrReport } from "../../../../infra/utils";
import { getCreditsWarning } from "../../../../business-logic/billingLogic";
import { toDatestampObject } from "../../../../models/time";
import { useBillingObservable } from "../../../../providers/BillingObservableProvider";
import { useBillingModeData } from "../../../../components/BillClientModal/BillingDate/useBillingModeData";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { BillMode } from "../../../../components/BillClientModal/BillClientModal.types";
import { FIRST_ITEM_INDEX } from "../../../../components/BillClientModal/constants";
import { createEmptyServiceItem } from "../../../../components/BillClientModal/BillClientItem.utils";
import { ServiceEffectiveDateCallout, ServicePendingAmendmentCallout } from "../../../../components/BillClientModal/Callouts/Callouts";
import { normalizeEffectiveDate } from "../../../../components/BillClientModal/BillingDate/BillingDate.utils";
const billServiceLoader = 'billService';
/** Allows control over billing of manual services (oneTimeManual/repeatableManual). */
export default function BillServiceModal(props) {
  const {
    serviceWithAmendments: service,
    relationship,
    manualServicesBilling,
    invoiceDateInfo,
    totalCredits,
    dismissModal,
    earliestBillableDate
  } = props;
  const {
    BILL_CLIENT
  } = useFeatureFlags();
  const {
    notifyToRefresh
  } = useBillingObservable();
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  const {
    billModeOptions,
    defaultMode,
    defaultIssueDate
  } = useBillingModeData({
    invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    earliestBillableDate
  });
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      items: [createEmptyServiceItem(service)],
      mode: defaultMode,
      issueDate: defaultIssueDate
    }
  });
  const [mode, issueDate] = form.watch(['mode', 'issueDate']);
  const creditsWarning = useMemo(() => getCreditsWarning(totalCredits), [totalCredits]);
  async function onSubmit(data) {
    const {
      mode
    } = data;
    overlayContext.startLoader(billServiceLoader);
    const {
      success
    } = await handleRequest(sendBillServiceRequest(data));
    if (!success) {
      overlayContext.showErrorToast(getBillServiceFailureToastText(mode, service.name), "bill-service-".concat(mode, "-error-toast"));
    } else {
      notifyToRefresh();
      dismissModal();
      overlayContext.showSuccessToast(getServiceSuccessToastText(mode, service.name), "bill-service-".concat(mode, "-success-toast"));
    }
    overlayContext.stopLoader(billServiceLoader);
  }
  function sendBillServiceRequest(data) {
    const {
      items,
      mode,
      issueDate
    } = data;
    const serviceItem = items[FIRST_ITEM_INDEX];
    const {
      id,
      quantity,
      cost,
      price
    } = serviceItem.service;
    const {
      note
    } = serviceItem;
    const serviceToBill = {
      serviceId: id,
      quantity: serviceCostModel.isVariablePrice(cost) && quantity ? utils.dollarsToCents(+quantity) : undefined,
      price: serviceCostModel.isRangePrice(cost) ? utils.dollarsToCents(+price) : undefined,
      note: note || undefined
    };
    const billNow = date => api.relationship.billCharges(relationship.id, {
      charges: [],
      requestedBillingTime: toDatestampObject(date || getNow()),
      servicesToBill: [serviceToBill]
    });
    const billLater = date => api.relationship.submitCharges(relationship.id, {
      charges: [],
      requestedBillingTime: toDatestampObject(date),
      servicesToBill: [serviceToBill]
    });
    switch (mode) {
      case BillMode.Upcoming:
        {
          if (!invoiceDateInfo) {
            const error = new Error('no upcoming invoice to bill');
            sendErrReport(error);
            throw error;
          }
          logBI(10568);
          return billLater(invoiceDateInfo.invoiceDraftDates.dates()[0]);
        }
      case BillMode.FutureDate:
        {
          logBI(10582, {
            serviceId: id
          });
          return billLater(issueDate);
        }
      case BillMode.Now:
        {
          if (isSameDay(issueDate, getNow())) {
            logBI(10152, {
              serviceId: id
            });
            return billNow();
          } else {
            logBI(10578, {
              serviceId: id
            });
            return billNow(issueDate);
          }
        }
    }
  }
  return /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(ManualBillModal, {
    title: "Bill Service",
    invoiceDateInfo: invoiceDateInfo,
    effectiveDate: relationship.effectiveDate,
    billModeOptions: billModeOptions,
    onCancel: dismissModal,
    onSubmit: form.handleSubmit(onSubmit),
    infoBoxContent: /*#__PURE__*/React.createElement("div", {
      className: styles.callouts
    }, !BILL_CLIENT && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CumulativeWarningCallout, {
      service: service,
      issueDate: issueDate,
      manualServiceBilling: manualServicesBilling[service.id],
      testid: "bill-service-cumulative"
    }), /*#__PURE__*/React.createElement(ServicePendingAmendmentCallout, {
      service: service,
      testid: "pending-amendment-callout"
    }), /*#__PURE__*/React.createElement(ServiceEffectiveDateCallout, {
      mode: mode,
      service: service,
      issueDate: issueDate,
      testid: "effective-date-callout",
      relationshipEffectiveDate: normalizeEffectiveDate(relationship.effectiveDate)
    })), creditsWarning && /*#__PURE__*/React.createElement(Callout, {
      "data-testid": "has-credits-callout",
      withBorder: true
    }, creditsWarning.text)),
    tableContent: /*#__PURE__*/React.createElement(BillServiceTableItem, {
      manualServiceBilling: manualServicesBilling[service.id],
      itemIndex: FIRST_ITEM_INDEX,
      relationshipEffectiveDate: normalizeEffectiveDate(relationship.effectiveDate),
      serviceItem: form.getValues('items')[FIRST_ITEM_INDEX]
    }),
    earliestBillableDate: earliestBillableDate
  }));
}